import React from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { Box, Button, Container, Drawer, IconButton } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import Typography from '@material-ui/core/Typography'
import { useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import Alert from '@material-ui/lab/Alert'
import { SMB_DEFAULT_SYNC_ERROR_MESSAGE } from '../../../errorConstants'
import PromoCodeInput from '../../Base/CartDrawer/PromoCodeInput'
import { formatAmount } from '../../../utility'
import CartFlyoutPaymentIcons from './SmbOrderFlowExpress/CartFlyoutPaymentIcons'

const useStyles = makeStyles((theme) => ({
    cartPreviewDrawerOverrides: {
        display: 'flex',
        height: '100%',
        minHeight: '70%',
        width: '45%',
        minWidth: 320,
        maxWidth: 500,
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            minWidth: 'unset',
        },
    },
    cartPreviewDrawerWrapper: {
        flexGrow: 1,
    },
    topBar: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `1rem`,
        fontSize: '1.1rem',
        backgroundColor: '#f4f4f4',
        borderBottom: `2px solid ${theme.palette.grey.main}`,
    },
    topBarCloseIcon: {
        color: theme.palette.black.main,
    },
    bottomBar: {
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        marginTop: '2rem',
    },
    errorMessageContainer: {
        marginBottom: '1rem',
    },
    bottomBarButtonContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(2),
    },
    bottomBarAddCardButton: {
        border: '2px solid',
    },
    bottomBarCheckoutButton: {
        marginBottom: theme.spacing(1),
        marginTop: '1rem',
    },
    cartTextLineContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        borderBottom: `2px solid ${theme.palette.grey.main}`,
        paddingBottom: '0.8rem',
        marginBottom: '1rem',
    },
    subtotalValue: {
        color: theme.palette.primary.main,
        fontWeight: 'bold',
        fontSize: '1.2rem',
    },
}))

const CartPreviewDrawer = ({
    open,
    addingToCart,
    applyPromoIsLoading,
    removePromoIsLoading,
    toggleCartPreview,
    proceedToCheckout,
    addAnotherCard,
    handleApplyPromoCode,
    handleRemovePromoCode,
    promoCodeRedemption,
    cartItems,
    cartItemCount,
    cartSubtotal,
    totalDiscount,
    errors,
}) => {
    const classes = useStyles()
    const syncError = useSelector(({ smbCart }) => smbCart.syncError)

    return (
        <Drawer
            id="cart-preview-drawer"
            classes={{ paper: classes.cartPreviewDrawerOverrides }}
            anchor="right"
            open={open}
            onClose={toggleCartPreview()}
        >
            <Box className={classes.cartPreviewDrawerWrapper}>
                <Container className={classes.topBar}>
                    <Box display="flex" flexDirection="row">
                        <div
                            style={{
                                marginLeft: 5,
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Your business cart ({cartItemCount} items)
                        </div>
                    </Box>
                    <IconButton
                        className={classes.topBarCloseIcon}
                        edge="end"
                        aria-label="close cart preview drawer"
                        component="span"
                        onClick={toggleCartPreview()}
                    >
                        <CloseIcon />
                    </IconButton>
                </Container>
                {cartItems()}
                {!addingToCart && (
                    <>
                        <Container className={classes.bottomBar}>
                            {!isEmpty(syncError) && syncError.message && (
                                <Alert
                                    severity="error"
                                    className={classes.errorMessageContainer}
                                >
                                    {syncError.message ===
                                    SMB_DEFAULT_SYNC_ERROR_MESSAGE ? (
                                        <>{syncError.message}</>
                                    ) : (
                                        <>
                                            {syncError.message}
                                            {!isEmpty(syncError?.errors) && (
                                                <>
                                                    :
                                                    {Object.entries(
                                                        syncError.errors
                                                    ).map(([key, value]) => {
                                                        return (
                                                            <React.Fragment
                                                                key={key}
                                                            >
                                                                {' '}
                                                                {value}.
                                                            </React.Fragment>
                                                        )
                                                    })}
                                                </>
                                            )}
                                        </>
                                    )}
                                </Alert>
                            )}
                            <Box className={classes.cartTextLineContainer}>
                                <Typography variant="body1">
                                    Shipping
                                </Typography>
                                <Typography variant="body1">
                                    Calculated during checkout
                                </Typography>
                            </Box>
                            {totalDiscount > 0 && (
                                <Box className={classes.cartTextLineContainer}>
                                    <Typography variant="body1">
                                        Promo Discount
                                    </Typography>
                                    <Typography variant="body1">
                                        -${formatAmount(totalDiscount)}
                                    </Typography>
                                </Box>
                            )}
                            <Box className={classes.cartTextLineContainer}>
                                <Typography
                                    variant="body1"
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: '1.2rem',
                                    }}
                                >
                                    Subtotal
                                </Typography>
                                <Typography
                                    variant="body1"
                                    className={classes.subtotalValue}
                                >
                                    ${cartSubtotal}
                                </Typography>
                            </Box>
                            <Box>
                                <PromoCodeInput
                                    appliedPromoCode={
                                        promoCodeRedemption?.redemption_code
                                    }
                                    appliedPromoCodeTerms={
                                        promoCodeRedemption?.terms
                                    }
                                    hasQualifiedItems={
                                        promoCodeRedemption?.has_qualified_items
                                    }
                                    applyPromoIsLoading={applyPromoIsLoading}
                                    removePromoIsLoading={removePromoIsLoading}
                                    errors={errors}
                                    handleApplyPromoCode={handleApplyPromoCode}
                                    handleRemovePromoCode={
                                        handleRemovePromoCode
                                    }
                                />
                            </Box>
                            <div className={classes.bottomBarButtonContainer}>
                                <Button
                                    id="checkout-button"
                                    className={classes.bottomBarCheckoutButton}
                                    variant="contained"
                                    color="primary"
                                    size={'large'}
                                    aria-label="proceed to checkout"
                                    onClick={proceedToCheckout}
                                    disabled={
                                        cartItemCount === 0 ||
                                        applyPromoIsLoading ||
                                        removePromoIsLoading
                                    }
                                >
                                    {applyPromoIsLoading
                                        ? 'Applying promo code...'
                                        : removePromoIsLoading
                                        ? 'Removing promo code...'
                                        : 'Proceed to checkout'}
                                </Button>
                                <Button
                                    className={classes.bottomBarAddCardButton}
                                    variant="outlined"
                                    color="primary"
                                    size={'large'}
                                    aria-label="add another card"
                                    onClick={addAnotherCard}
                                >
                                    Add another card
                                </Button>
                            </div>
                        </Container>
                        <CartFlyoutPaymentIcons />
                    </>
                )}
            </Box>
        </Drawer>
    )
}

export default CartPreviewDrawer
